/**
 * Mock catalog for `/marketplace`.
 *
 * The marketplace backend isn't wired yet (catalog endpoint + install
 * flow land in a follow-up PR — see `docs/adr/0001-marketplace-scope.md`).
 * For now the frontend ships a static catalog so the page is fully
 * clickable end-to-end and the install button does something believable.
 *
 * Marketplace scope (per ADR):
 *   - **Datapacks**: pre-built KBs the platform curates. Install = provision
 *     a read-only KB under the team.
 *   - **Agent templates**: ready-made agent configs (system prompt + tools +
 *     skills + LLM config). Install = clone a draft Agent under the team.
 *
 * Skills and MCP plugins are NOT in the marketplace — they have their own
 * dedicated surfaces (`/skills`, `/mcp`). If a datapack or agent template
 * depends on a skill/MCP, the install flow will auto-enable it.
 *
 * When the backend lands:
 *   1. Replace ``MOCK_CATALOG`` with an ``api<MarketplaceCatalogResponse>``
 *      call in the route loader.
 *   2. Replace the localStorage-backed install set with the real
 *      ``/v1/marketplace/installed`` + ``/v1/marketplace/install``
 *      endpoints.
 *   3. Delete this file. No other surface should depend on it.
 *
 * The shapes below match `MarketplaceItem` in `lib/types.ts` exactly,
 * so the swap is mechanical.
 */
import type { MarketplaceItem } from "@/lib/types";

export const MOCK_CATALOG: MarketplaceItem[] = [
  // ── Datapacks ────────────────────────────────────────────────────
  {
    id: "datapack.cn-baijiu",
    kind: "datapack",
    name: "白酒板块数据分析",
    tagline: "A 股白酒板块全量行情 + 基本面，开箱即用",
    description:
      "覆盖中证白酒指数 38 只成分股的近 5 年日线行情、季报财务数据、机构持仓与卖方研报摘要。安装后会在团队空间自动挂载一个只读知识库，Agent 通过 `kb_search` 直接引用并标注来源。",
    category: "金融行情",
    bundles: ["白酒板块 38 只股票", "5 年日线 + 分钟线", "财报基本面", "卖方研报摘要"],
    risk: "safe",
    version: "2026.05.r1",
    vendor: "knode-os",
    kb_slug: "marketplace-cn-baijiu",
  },
  {
    id: "datapack.cn-ashare-daily",
    kind: "datapack",
    name: "A 股全市场日线",
    tagline: "沪深京三市 5000+ 只股票的日线 + 分红送配",
    description:
      "全市场日 K、复权因子、停复牌、分红送配记录。每日 17:30 自动滚动更新。适合做大盘择时、行业轮动、因子回测等批量分析任务。",
    category: "金融行情",
    bundles: ["5000+ 只股票", "日线 / 周线 / 月线", "前复权 / 后复权", "公司行动事件"],
    risk: "safe",
    version: "2026.05.daily",
    vendor: "knode-os",
    kb_slug: "marketplace-cn-ashare-daily",
  },
  {
    id: "datapack.us-mag7",
    kind: "datapack",
    name: "美股七姐妹深度数据",
    tagline: "AAPL / MSFT / NVDA / GOOGL / AMZN / META / TSLA 全维度",
    description:
      "Magnificent Seven 的财报、电话会议纪要、SEC 文件、华尔街研报。中英双语字段，适合做中美科技股对比分析。",
    category: "金融行情",
    bundles: ["7 只龙头", "10-Q / 10-K 全文", "Earnings call 转写", "Top 10 卖方研报"],
    risk: "safe",
    version: "2026.05.q1",
    vendor: "knode-os",
    kb_slug: "marketplace-us-mag7",
  },
  {
    id: "datapack.esg-cn",
    kind: "datapack",
    name: "中证 ESG 评级数据",
    tagline: "国内 ESG 评级 + 争议事件追踪",
    description: "MSCI、商道融绿、华证 ESG 三家口径并列，覆盖沪深 300 全部成分股。",
    category: "ESG",
    bundles: ["三家评级并列", "争议事件时间线", "碳排放估算"],
    risk: "safe",
    version: "2026.04.r2",
    vendor: "knode-os",
    kb_slug: "marketplace-esg-cn",
  },
  {
    id: "datapack.cn-fund-nav",
    kind: "datapack",
    name: "公募基金净值 + 持仓",
    tagline: "全市场 1.2 万只公募基金的净值序列与季报持仓",
    description:
      "日频净值、累计净值、复权净值；季报前十大重仓股、行业配置比例。适合做 FOF 筛选、风格漂移检测。",
    category: "金融行情",
    bundles: ["1.2 万只基金", "日频净值", "季报持仓", "基金经理变更"],
    risk: "safe",
    version: "2026.05.r1",
    vendor: "knode-os",
    kb_slug: "marketplace-cn-fund-nav",
  },
  {
    id: "datapack.cn-regulations",
    kind: "datapack",
    name: "金融监管法规库",
    tagline: "证监会 / 银保监 / 央行 / 外管局全文法规",
    description:
      "2000 年至今的法规、规章、规范性文件全文 + 结构化元数据。适合合规审查、政策解读、监管变化追踪。",
    category: "合规",
    bundles: ["4 大监管机构", "全文 + 附件", "修订对照", "生效/废止状态"],
    risk: "safe",
    version: "2026.05.r1",
    vendor: "knode-os",
    kb_slug: "marketplace-cn-regulations",
  },

  // ── Agent templates ─────────────────────────────────────────────
  {
    id: "agent.equity-research",
    kind: "agents",
    name: "股票研究助手",
    tagline: "行情 + 财报 + 研报一站式投研分析",
    description:
      "预置 system prompt 引导多步投研流程：先拉行情概览，再查财报关键指标，最后综合卖方观点给出结论。绑定 research-deepdive skill + kb_search 工具。安装后在「智能体」里出现一份可编辑草稿。",
    category: "投研",
    bundles: ["research-deepdive skill", "kb_search 工具", "推荐 GPT-4o 配置"],
    risk: "safe",
    version: "1.0.0",
    vendor: "knode-os",
  },
  {
    id: "agent.compliance-reviewer",
    kind: "agents",
    name: "合规审查员",
    tagline: "自动比对文档与监管法规，输出合规意见",
    description:
      "system prompt 指导 Agent 先检索相关法规条文，再逐条比对用户上传的文档，最后输出结构化合规意见（通过/风险/建议）。绑定 doc-writer + kb_search。",
    category: "合规",
    bundles: ["doc-writer skill", "kb_search 工具", "法规库 datapack 推荐"],
    risk: "caution",
    version: "1.0.0",
    vendor: "knode-os",
  },
  {
    id: "agent.incident-responder",
    kind: "agents",
    name: "事故响应官",
    tagline: "SRE 事故处理 runbook 自动化",
    description:
      "预置 incident-runbook skill，引导 Agent 按 SEV 分级执行标准化响应流程：收集上下文 → 定位根因 → 生成 RCA 报告。适合 on-call 场景。",
    category: "运维",
    bundles: ["incident-runbook skill", "sandbox-shell 工具", "推荐 Claude 配置"],
    risk: "destructive",
    version: "1.0.0",
    vendor: "knode-os",
  },
  {
    id: "agent.sql-analyst",
    kind: "agents",
    name: "SQL 数据分析师",
    tagline: "自然语言转 SQL，自动执行并可视化",
    description:
      "绑定 sql-explorer skill + sandbox-shell 工具。用户用自然语言描述需求，Agent 生成 SQL、在沙箱执行、返回表格或图表。适合 BI 自助查询场景。",
    category: "数据分析",
    bundles: ["sql-explorer skill", "sandbox-shell 工具", "推荐 GPT-4o-mini 配置"],
    risk: "caution",
    version: "1.0.0",
    vendor: "knode-os",
  },
  {
    id: "agent.release-notes-writer",
    kind: "agents",
    name: "发版说明生成器",
    tagline: "从 Git log + JIRA 自动生成 Release Notes",
    description:
      "绑定 release-notes skill + code-reviewer skill。Agent 拉取最近的 commit / PR 信息，结合 JIRA ticket 状态，输出结构化的发版说明（面向用户 + 面向内部）。",
    category: "研发效能",
    bundles: ["release-notes skill", "code-reviewer skill", "MCP: GitHub / JIRA"],
    risk: "safe",
    version: "1.0.0",
    vendor: "knode-os",
  },
];

const INSTALLED_KEY = "kos:marketplace:installed";

/**
 * Read/write the set of installed marketplace item IDs from localStorage.
 * We persist across reloads so the mock UX feels real, but it's still
 * per-browser — no cross-device sync until the backend lands.
 */
export function readInstalledIds(): string[] {
  try {
    const raw = window.localStorage.getItem(INSTALLED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string");
  } catch {
    return [];
  }
}

export function writeInstalledIds(ids: string[]): void {
  try {
    window.localStorage.setItem(INSTALLED_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage may be disabled in private mode — best-effort. */
  }
}
